import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getValidToken } from '../utils/authUtils';
import { RelationshipMemoryViewer } from './admin/brokerage';

// ============================================
// EXECUTIVE PROFILE VIEWER
// ============================================
// Detailed profile page at /admin/executive-intelligence/profile/:id
// Shows: profile info, career timeline, education, skills,
// leadership scores, version history, and change events.
// ============================================

const ExecProfileViewer = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();

  // --- State ---
  const [profile, setProfile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [events, setEvents] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  // Bookmark State
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  // Relationship Memory Viewer State
  const [showRelationshipMemory, setShowRelationshipMemory] = useState(false);

  // Reference Score State
  const [referenceScore, setReferenceScore] = useState(null);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [referenceFormData, setReferenceFormData] = useState({
    human_score: '',
    human_rationale: '',
    human_trajectory_score: '',
    human_velocity_score: '',
    human_scope_score: '',
    human_recency_score: '',
  });
  const [referenceSubmitting, setReferenceSubmitting] = useState(false);
  const [referenceError, setReferenceError] = useState(null);

  // Reprocess State
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);

  // LinkedIn Comments State
  const [linkedinComments, setLinkedinComments] = useState([]);
  const [linkedinCommentsLoading, setLinkedinCommentsLoading] = useState(false);
  const [linkedinCommentsTotal, setLinkedinCommentsTotal] = useState(0);
  const [linkedinImporting, setLinkedinImporting] = useState(false);

  // Versium Predictive Scores state
  const [versiumScores, setVersiumScores] = useState(null);
  const [versiumLoading, setVersiumLoading] = useState(false);
  const [versiumError, setVersiumError] = useState(null);

  // Auth check
  useEffect(() => {
    const token = getValidToken();
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch all profile data
  useEffect(() => {
    if (profileId) {
      fetchProfileData();
    }
  }, [profileId]);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getValidToken()}` },
  });

  const toggleBookmark = async () => {
    setBookmarkLoading(true);
    try {
      if (isBookmarked) {
        await axios.delete(`${API_URL}/api/exec/bookmarks/${profileId}`, authHeaders());
      } else {
        await axios.post(`${API_URL}/api/exec/bookmarks/${profileId}`, {}, authHeaders());
      }
      setIsBookmarked(!isBookmarked);
    } catch (err) {
      console.error('Error toggling bookmark:', err);
    } finally {
      setBookmarkLoading(false);
    }
  };

  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, versionsRes, eventsRes, scoresRes, referenceRes, bookmarkRes] = await Promise.all([
        axios.get(`${API_URL}/api/exec/profiles/${profileId}`, authHeaders()),
        axios.get(`${API_URL}/api/exec/profiles/${profileId}/versions`, authHeaders()),
        axios.get(`${API_URL}/api/exec/profiles/${profileId}/events?page_size=100`, authHeaders()),
        axios.get(`${API_URL}/api/exec/profiles/${profileId}/scores`, authHeaders()),
        axios.get(`${API_URL}/api/exec/calibration/reference-scores/profile/${profileId}`, authHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/exec/bookmarks/check/${profileId}`, authHeaders()).catch(() => ({ data: { is_bookmarked: false } })),
      ]);
      setProfile(profileRes.data);
      setVersions(versionsRes.data || []);
      setEvents(eventsRes.data?.items || []);
      setScores(scoresRes.data || []);
      setReferenceScore(referenceRes.data);
      setIsBookmarked(bookmarkRes.data?.is_bookmarked || false);
      // Initialize Versium scores from profile data if present
      if (profileRes.data?.versium_scores) {
        setVersiumScores(profileRes.data.versium_scores);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    setReprocessResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/exec/profiles/${profileId}/reprocess`,
        {},
        authHeaders()
      );
      const d = res.data;
      const diag = d.raw_data_diagnostics || {};
      const counts = d.career_records || {};
      let message = d.message;
      // Warn if raw data was missing expected keys
      if (!diag.has_experience && !diag.has_education && !diag.has_skills) {
        message += ' WARNING: raw_profile has no experience/education/skills data. Keys found: ' + (diag.keys || []).join(', ');
      } else if (counts.roles === 0 && counts.education === 0 && counts.skills === 0) {
        message += ' WARNING: No records were extracted despite data being present.';
      }
      if (d.scoring_queued === false) {
        message += ' SCORING NOT QUEUED — check server logs.';
      }
      setReprocessResult({ success: d.scoring_queued !== false, message });
      await fetchProfileData();
    } catch (err) {
      setReprocessResult({
        success: false,
        message: err.response?.data?.detail || 'Reprocess failed',
      });
    } finally {
      setReprocessing(false);
      setTimeout(() => setReprocessResult(null), 8000);
    }
  };

  // --- Helper components ---

  const ScoreBadge = ({ band, size = 'sm' }) => {
    const colors = {
      emerging: 'bg-gray-100 text-gray-700',
      developing: 'bg-blue-100 text-blue-700',
      established: 'bg-green-100 text-green-700',
      senior: 'bg-purple-100 text-purple-700',
      executive: 'bg-amber-100 text-amber-800',
    };
    const sizeClass = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
    return (
      <span className={`rounded-full font-medium ${sizeClass} ${colors[band] || 'bg-gray-100 text-gray-600'}`}>
        {band || 'unscored'}
      </span>
    );
  };

  const LevelBadge = ({ level }) => {
    if (!level) return <span className="text-xs text-gray-400">—</span>;
    const colors = {
      c_suite: 'bg-red-100 text-red-800',
      board: 'bg-red-100 text-red-800',
      vp: 'bg-orange-100 text-orange-800',
      svp: 'bg-orange-100 text-orange-800',
      evp: 'bg-orange-100 text-orange-800',
      director: 'bg-yellow-100 text-yellow-800',
      senior_director: 'bg-yellow-100 text-yellow-800',
      manager: 'bg-blue-100 text-blue-800',
      senior_manager: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[level] || 'bg-gray-100 text-gray-600'}`}>
        {level.replace('_', ' ')}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const calculateDuration = (start, end) => {
    if (!start) return '';
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years > 0 && remainingMonths > 0) return `${years}y ${remainingMonths}m`;
    if (years > 0) return `${years}y`;
    return `${months}m`;
  };

  // --- Reference Score Functions ---
  const getBandForScore = (score) => {
    if (score < 2) return 'emerging';
    if (score < 4) return 'developing';
    if (score < 6) return 'established';
    if (score < 8) return 'senior';
    return 'executive';
  };

  const handleSubmitReferenceScore = async () => {
    if (!referenceFormData.human_score) {
      setReferenceError('Overall score is required');
      return;
    }
    const score = parseFloat(referenceFormData.human_score);
    if (isNaN(score) || score < 0 || score > 10) {
      setReferenceError('Score must be between 0 and 10');
      return;
    }

    setReferenceSubmitting(true);
    setReferenceError(null);

    try {
      const payload = {
        exec_profile_id: profileId,
        human_score: score,
        human_band: getBandForScore(score),
        human_rationale: referenceFormData.human_rationale || null,
        human_trajectory_score: referenceFormData.human_trajectory_score ? parseFloat(referenceFormData.human_trajectory_score) : null,
        human_velocity_score: referenceFormData.human_velocity_score ? parseFloat(referenceFormData.human_velocity_score) : null,
        human_scope_score: referenceFormData.human_scope_score ? parseFloat(referenceFormData.human_scope_score) : null,
        human_recency_score: referenceFormData.human_recency_score ? parseFloat(referenceFormData.human_recency_score) : null,
      };

      const res = await axios.post(`${API_URL}/api/exec/calibration/reference-scores`, payload, authHeaders());
      setReferenceScore(res.data);
      setShowReferenceModal(false);
      setReferenceFormData({
        human_score: '',
        human_rationale: '',
        human_trajectory_score: '',
        human_velocity_score: '',
        human_scope_score: '',
        human_recency_score: '',
      });
    } catch (err) {
      console.error('Error submitting reference score:', err);
      setReferenceError(err.response?.data?.detail || 'Failed to submit reference score');
    } finally {
      setReferenceSubmitting(false);
    }
  };

  const handleDeleteReferenceScore = async () => {
    if (!referenceScore?.id) return;
    if (!window.confirm('Are you sure you want to delete this reference score?')) return;

    try {
      await axios.delete(`${API_URL}/api/exec/calibration/reference-scores/${referenceScore.id}`, authHeaders());
      setReferenceScore(null);
    } catch (err) {
      console.error('Error deleting reference score:', err);
      alert(err.response?.data?.detail || 'Failed to delete reference score');
    }
  };

  const openEditReferenceModal = () => {
    if (referenceScore) {
      setReferenceFormData({
        human_score: referenceScore.human_score?.toString() || '',
        human_rationale: referenceScore.human_rationale || '',
        human_trajectory_score: referenceScore.human_trajectory_score?.toString() || '',
        human_velocity_score: referenceScore.human_velocity_score?.toString() || '',
        human_scope_score: referenceScore.human_scope_score?.toString() || '',
        human_recency_score: referenceScore.human_recency_score?.toString() || '',
      });
    }
    setShowReferenceModal(true);
  };

  // --- LinkedIn Comments ---
  const fetchLinkedInComments = async (execPersonId) => {
    if (!execPersonId) return;
    setLinkedinCommentsLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/exec/comments/${execPersonId}?page_size=50`,
        authHeaders()
      );
      setLinkedinComments(res.data.items || []);
      setLinkedinCommentsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching LinkedIn comments:', err);
    } finally {
      setLinkedinCommentsLoading(false);
    }
  };

  const handleImportComments = async () => {
    const p = profile;
    if (!p?.exec_person_id) return;
    setLinkedinImporting(true);
    try {
      await axios.post(
        `${API_URL}/api/exec/comments/import/single`,
        { exec_person_id: p.exec_person_id, max_pages: 1 },
        authHeaders()
      );
      fetchLinkedInComments(p.exec_person_id);
    } catch (err) {
      console.error('Error importing comments:', err);
      setError('Comment import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLinkedinImporting(false);
    }
  };

  // Versium score fetch
  const fetchVersiumScore = async (scoreType) => {
    if (!profile?.exec_contact_id) {
      setVersiumError('No contact record linked to this profile');
      return;
    }
    setVersiumLoading(true);
    setVersiumError(null);
    try {
      const outputTypes = scoreType === 'both' ? ['donor_score', 'wealth_score'] : [scoreType];
      const res = await axios.post(
        `${API_URL}/api/exec/contacts/${profile.exec_contact_id}/versium-scores`,
        { output_types: outputTypes, force_refresh: true },
        authHeaders()
      );
      setVersiumScores(res.data);
    } catch (err) {
      setVersiumError(err.response?.data?.detail || 'Failed to fetch scores');
    } finally {
      setVersiumLoading(false);
    }
  };

  // Lazy-load comments when tab is selected
  useEffect(() => {
    if (activeSection === 'linkedin' && profile?.exec_person_id && linkedinComments.length === 0 && !linkedinCommentsLoading) {
      fetchLinkedInComments(profile.exec_person_id);
    }
  }, [activeSection, profile]);

  // --- Section navigation ---
  const SECTIONS = [
    { key: 'overview', label: 'Overview' },
    { key: 'timeline', label: 'Career Timeline' },
    { key: 'education', label: 'Education' },
    { key: 'skills', label: 'Skills' },
    { key: 'scores', label: 'Leadership Scores' },
    { key: 'versions', label: 'Version History' },
    { key: 'events', label: 'Change Log' },
    { key: 'linkedin', label: 'LinkedIn Comments' },
  ];

  // --- Render: Loading/Error states ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-red-200 rounded-lg p-6 max-w-md text-center">
          <div className="text-red-600 text-lg mb-2">Error</div>
          <div className="text-gray-600">{error}</div>
          <button
            onClick={() => navigate('/admin/executive-intelligence?tab=search')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const p = profile;
  const latestScore = p.latest_score;

  // --- Render: Overview Section ---
  const renderOverview = () => (
    <div className="space-y-4">
      {/* Profile Header Card */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex gap-4 items-start">
            {p.profile_picture_url ? (
              <img
                src={p.profile_picture_url}
                alt={p.full_name}
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-2xl font-bold shrink-0 border-2 border-gray-200">
                {(p.first_name?.[0] || '').toUpperCase()}{(p.last_name?.[0] || '').toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">{p.full_name || 'Unknown'}</h2>
                <button
                  onClick={toggleBookmark}
                  disabled={bookmarkLoading}
                  className={`text-2xl transition-colors ${
                    isBookmarked
                      ? 'text-yellow-500 hover:text-yellow-600'
                      : 'text-gray-300 hover:text-yellow-400'
                  } ${bookmarkLoading ? 'opacity-50 cursor-wait' : ''}`}
                  title={isBookmarked ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {isBookmarked ? '★' : '☆'}
                </button>
              </div>
              {p.headline && <div className="text-gray-600 mt-1">{p.headline}</div>}
              <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-500">
                {p.current_title && <span>{p.current_title}</span>}
                {p.current_title && p.current_company && <span>at</span>}
                {p.current_company && <span className="font-medium">{p.current_company}</span>}
              </div>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
                {p.location_raw && <span>{p.location_raw}</span>}
                {p.open_to_work && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    Open to Work
                  </span>
                )}
                {p.is_linkedin_follower && (
                  <span className="px-2 py-0.5 bg-emerald-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    Follows Us{p.linkedin_connected_on && ` - ${new Date(p.linkedin_connected_on).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
                  </span>
                )}
              </div>
              {/* Contact Info - Stacked Vertically */}
              {(p.profile_url || p.alumni_community_email || p.email || p.personal_email || p.personal_email_2 || p.personal_email_3 || p.personal_email_4) && (
                <div className="flex flex-col gap-1 mt-3 text-sm">
                  {p.profile_url && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium w-20">LinkedIn:</span>
                      <a href={p.profile_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1">
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                        View Profile
                      </a>
                    </div>
                  )}
                  {p.alumni_community_email && (
                    <div className="flex items-center gap-2">
                      <span className="text-amber-500 text-xs font-medium w-20">Community:</span>
                      <a href={`mailto:${p.alumni_community_email}`} className="text-amber-600 hover:text-amber-700 hover:underline">
                        {p.alumni_community_email}
                      </a>
                    </div>
                  )}
                  {p.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium w-20">LinkedIn Email:</span>
                      <a href={`mailto:${p.email}`} className="text-indigo-500 hover:text-indigo-700 hover:underline">
                        {p.email}
                      </a>
                    </div>
                  )}
                  {p.personal_email && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium w-20">Personal:</span>
                      <a href={`mailto:${p.personal_email}`} className="text-emerald-600 hover:text-emerald-700 hover:underline">
                        {p.personal_email}
                      </a>
                    </div>
                  )}
                  {p.personal_email_2 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium w-20">Personal 2:</span>
                      <a href={`mailto:${p.personal_email_2}`} className="text-emerald-600 hover:text-emerald-700 hover:underline">
                        {p.personal_email_2}
                      </a>
                    </div>
                  )}
                  {p.personal_email_3 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium w-20">Personal 3:</span>
                      <a href={`mailto:${p.personal_email_3}`} className="text-emerald-600 hover:text-emerald-700 hover:underline">
                        {p.personal_email_3}
                      </a>
                    </div>
                  )}
                  {p.personal_email_4 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium w-20">Personal 4:</span>
                      <a href={`mailto:${p.personal_email_4}`} className="text-emerald-600 hover:text-emerald-700 hover:underline">
                        {p.personal_email_4}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Score Summary */}
          {latestScore && (
            <div className="text-center p-4 bg-gray-50 rounded-lg min-w-36">
              <div className="text-3xl font-bold text-indigo-600">{latestScore.score.toFixed(1)}</div>
              <ScoreBadge band={latestScore.score_band} size="lg" />
              <div className="text-xs text-gray-400 mt-2">
                Expires {formatDate(latestScore.expires_at)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Metadata Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-400">Source</div>
          <div className="text-sm font-medium mt-1">{p.source_system || '—'}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-400">Followers</div>
          <div className="text-sm font-medium mt-1">{p.follower_count != null ? p.follower_count.toLocaleString() : '—'}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-400">Connections</div>
          <div className="text-sm font-medium mt-1">{p.connection_count != null ? p.connection_count.toLocaleString() : '—'}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-400">Last Ingested</div>
          <div className="text-sm font-medium mt-1">{formatDate(p.last_ingested_at)}</div>
        </div>
      </div>

      {/* Versium Predictive Scores */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-violet-800">Predictive Scores (Versium)</h3>
          {versiumScores?.fetched_at && (
            <span className="text-xs text-violet-400">
              Fetched {new Date(versiumScores.fetched_at).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-6 mb-3">
          {versiumScores && (versiumScores.donor_score != null || versiumScores.wealth_score != null) ? (
            <>
              <div className="text-center">
                <div className="text-2xl font-bold text-violet-600">{versiumScores.donor_score ?? '—'}</div>
                <div className="text-xs text-violet-500">Giving Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-violet-600">{versiumScores.wealth_score ?? '—'}</div>
                <div className="text-xs text-violet-500">Wealth Score</div>
              </div>
            </>
          ) : (
            <div className="text-sm text-violet-400">No scores yet</div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => fetchVersiumScore('donor_score')}
            disabled={versiumLoading}
            className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
          >
            {versiumLoading ? 'Loading...' : 'Get Giving Score'}
          </button>
          <button
            onClick={() => fetchVersiumScore('wealth_score')}
            disabled={versiumLoading}
            className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
          >
            {versiumLoading ? 'Loading...' : 'Get Wealth Score'}
          </button>
          <button
            onClick={() => fetchVersiumScore('both')}
            disabled={versiumLoading}
            className="px-3 py-1.5 text-xs bg-violet-700 text-white rounded-lg hover:bg-violet-800 disabled:opacity-50"
          >
            {versiumLoading ? 'Loading...' : 'Get Both'}
          </button>
        </div>
        <div className="mt-2 text-xs text-violet-400">
          {p.has_address
            ? 'Address available (higher accuracy)'
            : 'No address on file — results may be less accurate'}
        </div>
        {versiumError && <div className="mt-2 text-xs text-red-600">{versiumError}</div>}
      </div>

      {/* Sub-Score Breakdown */}
      {latestScore && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Score Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Trajectory', value: latestScore.trajectory_score },
              { label: 'Velocity', value: latestScore.velocity_score },
              { label: 'Scope', value: latestScore.scope_score },
              { label: 'Recency', value: latestScore.recency_score },
            ].map(({ label, value }) => (
              <div key={label} className="text-center">
                <div className="text-xl font-bold text-gray-800">{value != null ? value.toFixed(1) : '—'}</div>
                <div className="text-xs text-gray-500">{label}</div>
                {value != null && (
                  <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 rounded-full"
                      style={{ width: `${(value / 10) * 100}%` }}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {latestScore.rationale && (
            <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
              <span className="font-medium text-gray-700">Rationale:</span> {latestScore.rationale}
            </div>
          )}
        </div>
      )}

      {/* About */}
      {p.about && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">About</h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{p.about}</p>
        </div>
      )}

      {/* Profile IDs */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">System Info</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-400">Profile ID:</span> <span className="font-mono">{p.id}</span></div>
          <div><span className="text-gray-400">Person ID:</span> <span className="font-mono">{p.exec_person_id || '—'}</span></div>
          <div><span className="text-gray-400">Stable Hash:</span> <span className="font-mono">{p.stable_hash || '—'}</span></div>
          <div><span className="text-gray-400">Created:</span> {formatDateTime(p.created_at)}</div>
        </div>
      </div>
    </div>
  );

  // --- Render: Career Timeline ---
  const renderTimeline = () => {
    const roles = p.roles || [];
    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">
            Career Timeline
            <span className="text-gray-400 font-normal ml-2">({roles.length} role{roles.length !== 1 ? 's' : ''})</span>
          </h3>
        </div>

        {roles.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">
            No roles recorded
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            {roles.map((role, idx) => (
              <div key={role.id} className="relative pl-14 pb-6">
                {/* Timeline dot */}
                <div className={`absolute left-4.5 w-3 h-3 rounded-full border-2 ${
                  role.is_current
                    ? 'bg-indigo-600 border-indigo-600'
                    : 'bg-white border-gray-300'
                }`} style={{ left: '18px', top: '6px' }}></div>

                <div className={`bg-white border rounded-lg shadow-sm p-4 ${
                  role.is_current ? 'border-indigo-200' : 'border-gray-200'
                }`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{role.title || 'Untitled'}</span>
                        {role.is_current && (
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-0.5">
                        {role.company_name || 'Unknown Company'}
                        {role.company_industry && (
                          <span className="text-gray-400"> ({role.company_industry})</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500 shrink-0">
                      <div>{formatDate(role.start_date)} — {role.is_current ? 'Present' : formatDate(role.end_date)}</div>
                      <div className="text-xs text-gray-400">{calculateDuration(role.start_date, role.end_date)}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    <LevelBadge level={role.role_level_override || role.role_level_inferred} />
                    {role.role_function && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{role.role_function}</span>
                    )}
                    {role.role_category && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{role.role_category}</span>
                    )}
                    {role.classification_confidence != null && (
                      <span className="text-xs text-gray-400">
                        conf: {(role.classification_confidence * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>

                  {role.skills_used && role.skills_used.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(Array.isArray(role.skills_used) ? role.skills_used : [role.skills_used]).map((s, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-xs">{s}</span>
                      ))}
                    </div>
                  )}

                  {role.location && (
                    <div className="mt-2 text-xs text-gray-400">{role.location}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // --- Render: Education ---
  const renderEducation = () => {
    const edu = p.education || [];
    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700">
            Education
            <span className="text-gray-400 font-normal ml-2">({edu.length})</span>
          </h3>
        </div>
        {edu.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">No education records</div>
        ) : (
          <div className="space-y-3">
            {edu.map((e) => (
              <div key={e.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <div className="font-medium text-gray-900">{e.institution || 'Unknown Institution'}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {e.degree && <span>{e.degree}</span>}
                  {e.degree && e.field_of_study && <span> in </span>}
                  {e.field_of_study && <span>{e.field_of_study}</span>}
                </div>
                {(e.start_year || e.end_year) && (
                  <div className="text-xs text-gray-400 mt-1">
                    {e.start_year && <span>{e.start_year}</span>}
                    {e.start_year && e.end_year && <span> — </span>}
                    {e.end_year && <span>{e.end_year}</span>}
                  </div>
                )}
                {e.honors && <div className="text-xs text-gray-500 mt-1">Honors: {e.honors}</div>}
                {e.activities && <div className="text-xs text-gray-500 mt-1">Activities: {e.activities}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // --- Render: Skills ---
  const renderSkills = () => {
    const skills = p.skills || [];
    const grouped = {};
    skills.forEach(s => {
      const cat = s.skill_category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(s);
    });

    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700">
            Skills
            <span className="text-gray-400 font-normal ml-2">({skills.length})</span>
          </h3>
        </div>
        {skills.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">No skills recorded</div>
        ) : (
          <div className="space-y-3">
            {Object.entries(grouped).map(([category, catSkills]) => (
              <div key={category} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">{category}</div>
                <div className="flex flex-wrap gap-2">
                  {catSkills.map((s, i) => (
                    <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-sm">
                      {s.skill_name}
                      {s.endorsement_count > 0 && (
                        <span className="text-indigo-400 ml-1 text-xs">({s.endorsement_count})</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // --- Render: Leadership Scores ---
  const renderScores = () => (
    <div className="space-y-4">
      {/* Human Reference Score Section */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Human Reference Score
            </h3>
            <p className="text-xs text-amber-600 mt-1">Expert assessment for calibration comparison</p>
          </div>
          {!referenceScore ? (
            <button
              onClick={() => setShowReferenceModal(true)}
              className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
            >
              + Add Reference Score
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={openEditReferenceModal}
                className="px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-200 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleDeleteReferenceScore}
                className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        {referenceScore ? (
          <div className="bg-white rounded-lg p-4 border border-amber-100">
            <div className="flex items-center gap-4 mb-3">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-700">{parseFloat(referenceScore.human_score).toFixed(1)}</div>
                <ScoreBadge band={referenceScore.human_band} size="lg" />
              </div>
              <div className="flex-1 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Scored by:</span>
                  <span className="font-medium">{referenceScore.scored_by}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-gray-400">Date:</span>
                  <span>{formatDateTime(referenceScore.scored_at)}</span>
                </div>
              </div>
            </div>

            {/* Sub-scores if provided */}
            {(referenceScore.human_trajectory_score != null ||
              referenceScore.human_velocity_score != null ||
              referenceScore.human_scope_score != null ||
              referenceScore.human_recency_score != null) && (
              <div className="grid grid-cols-4 gap-4 mb-3 pt-3 border-t border-amber-100">
                {[
                  { label: 'Trajectory', value: referenceScore.human_trajectory_score },
                  { label: 'Velocity', value: referenceScore.human_velocity_score },
                  { label: 'Scope', value: referenceScore.human_scope_score },
                  { label: 'Recency', value: referenceScore.human_recency_score },
                ].map(({ label, value }) => (
                  <div key={label} className="text-center">
                    <div className="text-sm font-medium text-amber-700">
                      {value != null ? parseFloat(value).toFixed(1) : '—'}
                    </div>
                    <div className="text-xs text-gray-400">{label}</div>
                  </div>
                ))}
              </div>
            )}

            {referenceScore.human_rationale && (
              <div className="p-3 bg-amber-50 rounded text-sm text-gray-600 mt-3">
                <span className="font-medium text-amber-700">Rationale:</span> {referenceScore.human_rationale}
              </div>
            )}

            {/* Comparison with AI Score */}
            {latestScore && (
              <div className="mt-3 pt-3 border-t border-amber-100">
                <div className="text-xs font-medium text-gray-500 mb-2">Comparison with AI Score</div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 grid grid-cols-3 gap-2 text-center text-sm">
                    <div>
                      <div className="text-amber-700 font-bold">{parseFloat(referenceScore.human_score).toFixed(1)}</div>
                      <div className="text-xs text-gray-400">Human</div>
                    </div>
                    <div>
                      <div className="text-indigo-600 font-bold">{latestScore.score.toFixed(1)}</div>
                      <div className="text-xs text-gray-400">AI</div>
                    </div>
                    <div>
                      <div className={`font-bold ${
                        Math.abs(parseFloat(referenceScore.human_score) - latestScore.score) <= 0.5 ? 'text-green-600' :
                        Math.abs(parseFloat(referenceScore.human_score) - latestScore.score) <= 1.0 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {(parseFloat(referenceScore.human_score) - latestScore.score) >= 0 ? '+' : ''}
                        {(parseFloat(referenceScore.human_score) - latestScore.score).toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-400">Diff</div>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    referenceScore.human_band === latestScore.score_band
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {referenceScore.human_band === latestScore.score_band ? 'Bands Match' : 'Band Mismatch'}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white/50 rounded-lg p-6 text-center border border-amber-100 border-dashed">
            <div className="text-amber-600 text-sm">No human reference score yet</div>
            <div className="text-xs text-amber-500 mt-1">Click "Add Reference Score" to provide expert assessment</div>
          </div>
        )}
      </div>

      {/* AI Score History */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700">
          AI Leadership Score History
          <span className="text-gray-400 font-normal ml-2">({scores.length})</span>
        </h3>
      </div>
      {scores.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">
          No scores recorded. Profile has not been scored yet.
        </div>
      ) : (
        <div className="space-y-3">
          {scores.map((s, idx) => (
            <div key={s.id} className={`bg-white border rounded-lg shadow-sm p-4 ${
              s.is_active ? 'border-indigo-200' : 'border-gray-200 opacity-75'
            }`}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold text-gray-800">{s.score.toFixed(1)}</div>
                  <div>
                    <ScoreBadge band={s.score_band} />
                    {s.is_active && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>
                    )}
                    {!s.is_active && (
                      <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">Expired</span>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs text-gray-400">
                  <div>Computed: {formatDateTime(s.computed_at)}</div>
                  <div>Expires: {formatDateTime(s.expires_at)}</div>
                </div>
              </div>

              {/* Sub-scores */}
              <div className="grid grid-cols-4 gap-4 mt-3">
                {[
                  { label: 'Trajectory', value: s.trajectory_score },
                  { label: 'Velocity', value: s.velocity_score },
                  { label: 'Scope', value: s.scope_score },
                  { label: 'Recency', value: s.recency_score },
                ].map(({ label, value }) => (
                  <div key={label} className="text-center">
                    <div className="text-sm font-medium text-gray-700">{value != null ? value.toFixed(1) : '—'}</div>
                    <div className="text-xs text-gray-400">{label}</div>
                  </div>
                ))}
              </div>

              {s.rationale && (
                <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-600">
                  {s.rationale}
                </div>
              )}

              {s.evidence && Object.keys(s.evidence).length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-indigo-600 cursor-pointer hover:text-indigo-800">
                    View evidence
                  </summary>
                  <pre className="mt-2 text-xs text-gray-500 bg-gray-50 p-3 rounded overflow-auto max-h-48 whitespace-pre-wrap">
                    {JSON.stringify(s.evidence, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // --- Render: Version History ---
  const renderVersions = () => (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700">
          Version History
          <span className="text-gray-400 font-normal ml-2">({versions.length})</span>
        </h3>
      </div>
      {versions.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">No versions recorded</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Version</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Captured At</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Stable Hash</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Batch</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {versions.map((v, idx) => (
                <tr key={v.id} className={`hover:bg-gray-50 ${idx === 0 ? 'bg-indigo-50' : ''}`}>
                  <td className="px-4 py-2">
                    <span className="font-mono text-xs">{v.id.substring(0, 8)}...</span>
                    {idx === 0 && <span className="ml-2 text-xs text-indigo-600 font-medium">Latest</span>}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{formatDateTime(v.captured_at)}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{v.stable_hash?.substring(0, 12)}...</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-400">
                    {v.import_batch_id ? v.import_batch_id.substring(0, 8) + '...' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // --- Render: LinkedIn Comments ---
  const renderLinkedInComments = () => (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            LinkedIn Comments
            <span className="text-gray-400 font-normal ml-2">({linkedinCommentsTotal})</span>
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => fetchLinkedInComments(profile?.exec_person_id)}
              disabled={linkedinCommentsLoading}
              className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              {linkedinCommentsLoading ? 'Loading...' : 'Refresh'}
            </button>
            <button
              onClick={handleImportComments}
              disabled={linkedinImporting}
              className="px-3 py-1.5 text-xs bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
            >
              {linkedinImporting ? 'Importing...' : 'Import Comments'}
            </button>
          </div>
        </div>
      </div>
      {linkedinCommentsLoading && linkedinComments.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">
          Loading LinkedIn comments...
        </div>
      ) : linkedinComments.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-400">No LinkedIn comments imported yet</p>
          <p className="text-xs text-gray-300 mt-1">Click "Import Comments" to scrape this person's public LinkedIn comment activity.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {linkedinComments.map(c => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-xs text-gray-500">
                  {c.post_author_name && (
                    <span>Commented on <span className="font-medium text-gray-700">{c.post_author_name}</span>'s post</span>
                  )}
                  {c.post_author_headline && (
                    <span className="text-gray-400"> &middot; {c.post_author_headline}</span>
                  )}
                  {c.commented_at && (
                    <span> &middot; {new Date(c.commented_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  )}
                </div>
                {c.post_url && (
                  <a href={c.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline flex-shrink-0 ml-2">
                    View on LinkedIn
                  </a>
                )}
              </div>
              {c.post_text_snippet && (
                <div className="text-xs text-gray-400 italic mb-2 border-l-2 border-gray-200 pl-2 line-clamp-2">
                  {c.post_text_snippet}
                </div>
              )}
              <p className="text-sm text-gray-800">{c.comment_text}</p>
              {(c.num_likes > 0 || c.num_replies > 0) && (
                <div className="flex gap-3 mt-2 text-xs text-gray-500">
                  {c.num_likes > 0 && <span>{c.num_likes} like{c.num_likes !== 1 ? 's' : ''}</span>}
                  {c.num_replies > 0 && <span>{c.num_replies} repl{c.num_replies !== 1 ? 'ies' : 'y'}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // --- Render: Change Events ---
  const renderEvents = () => (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700">
          Change Log
          <span className="text-gray-400 font-normal ml-2">({events.length})</span>
        </h3>
      </div>
      {events.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400">No change events recorded</div>
      ) : (
        <div className="space-y-2">
          {events.map((e) => {
            const typeColors = {
              role_added: 'border-l-green-500',
              role_removed: 'border-l-red-500',
              role_changed: 'border-l-blue-500',
              title_changed: 'border-l-yellow-500',
              company_changed: 'border-l-orange-500',
              education_added: 'border-l-green-500',
              education_removed: 'border-l-red-500',
              skill_added: 'border-l-green-500',
              skill_removed: 'border-l-red-500',
            };
            return (
              <div
                key={e.id}
                className={`bg-white border border-gray-200 border-l-4 ${typeColors[e.event_type] || 'border-l-gray-400'} rounded-r-lg shadow-sm p-3`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-sm font-medium text-gray-800">
                      {e.event_type?.replace(/_/g, ' ')}
                    </span>
                    {e.entity_type && (
                      <span className="text-xs text-gray-400 ml-2">({e.entity_type})</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">{formatDateTime(e.detected_at)}</span>
                </div>
                {e.entity_key && (
                  <div className="text-xs text-gray-500 mt-1">Key: {e.entity_key}</div>
                )}
                {(e.old_value || e.new_value) && (
                  <div className="mt-2 text-xs grid grid-cols-2 gap-2">
                    {e.old_value && (
                      <div className="p-2 bg-red-50 rounded">
                        <span className="text-red-500 font-medium">Old: </span>
                        <span className="text-gray-600">
                          {typeof e.old_value === 'object' ? JSON.stringify(e.old_value) : String(e.old_value)}
                        </span>
                      </div>
                    )}
                    {e.new_value && (
                      <div className="p-2 bg-green-50 rounded">
                        <span className="text-green-500 font-medium">New: </span>
                        <span className="text-gray-600">
                          {typeof e.new_value === 'object' ? JSON.stringify(e.new_value) : String(e.new_value)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/executive-intelligence?tab=search')}
                className="text-gray-400 hover:text-gray-600"
              >
                &larr;
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{p.full_name || 'Profile'}</h1>
                <p className="text-xs text-gray-500">{p.headline || p.current_title || ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {p.exec_person_id && (
                <button
                  onClick={() => setShowRelationshipMemory(true)}
                  className="px-4 py-2 text-sm border border-teal-300 rounded-lg hover:bg-teal-50 text-teal-700 flex items-center gap-2"
                  title="View relationship memory and engagement history"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Relationship Memory
                </button>
              )}
              <button
                onClick={handleReprocess}
                disabled={reprocessing}
                className="px-4 py-2 text-sm border border-indigo-300 rounded-lg hover:bg-indigo-50 text-indigo-700 flex items-center gap-2 disabled:opacity-50"
                title="Reprocess raw LinkedIn data to create career records and trigger scoring"
              >
                {reprocessing ? (
                  <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-700 rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                {reprocessing ? 'Reprocessing...' : 'Reprocess Profile'}
              </button>
              <button
                onClick={() => navigate('/admin/executive-intelligence')}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
              >
                Dashboard
              </button>
            </div>
          </div>

          {/* Reprocess result toast */}
          {reprocessResult && (
            <div className={`mx-4 mt-2 px-4 py-2 rounded-lg text-sm ${
              reprocessResult.success
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {reprocessResult.message}
            </div>
          )}

          {/* Section Navigation */}
          <div className="flex gap-1 overflow-x-auto pb-0 -mb-px">
            {SECTIONS.map(sec => (
              <button
                key={sec.key}
                onClick={() => setActiveSection(sec.key)}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === sec.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {sec.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeSection === 'overview' && renderOverview()}
        {activeSection === 'timeline' && renderTimeline()}
        {activeSection === 'education' && renderEducation()}
        {activeSection === 'skills' && renderSkills()}
        {activeSection === 'scores' && renderScores()}
        {activeSection === 'versions' && renderVersions()}
        {activeSection === 'events' && renderEvents()}
        {activeSection === 'linkedin' && renderLinkedInComments()}
      </div>

      {/* Relationship Memory Modal */}
      {showRelationshipMemory && p.exec_person_id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Relationship Memory</h3>
                <p className="text-sm text-gray-500">{p.full_name}</p>
              </div>
              <button
                onClick={() => setShowRelationshipMemory(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <RelationshipMemoryViewer
                execPersonId={p.exec_person_id}
                execName={p.full_name}
                onClose={() => setShowRelationshipMemory(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Reference Score Modal */}
      {showReferenceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {referenceScore ? 'Edit' : 'Add'} Reference Score
                </h3>
                <button
                  onClick={() => {
                    setShowReferenceModal(false);
                    setReferenceError(null);
                    if (!referenceScore) {
                      setReferenceFormData({
                        human_score: '',
                        human_rationale: '',
                        human_trajectory_score: '',
                        human_velocity_score: '',
                        human_scope_score: '',
                        human_recency_score: '',
                      });
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Profile Context */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-900">{p.full_name}</div>
                <div className="text-sm text-gray-500">{p.current_title} at {p.current_company}</div>
                {latestScore && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-400">Current AI Score:</span>
                    <span className="text-sm font-bold text-indigo-600">{latestScore.score.toFixed(1)}</span>
                    <ScoreBadge band={latestScore.score_band} />
                  </div>
                )}
              </div>

              {referenceError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {referenceError}
                </div>
              )}

              {/* Overall Score (Required) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Overall Score <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={referenceFormData.human_score}
                  onChange={(e) => setReferenceFormData(prev => ({ ...prev, human_score: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="0.0 - 10.0"
                />
                <p className="text-xs text-gray-400 mt-1">
                  0-2: Emerging | 2-4: Developing | 4-6: Established | 6-8: Senior | 8-10: Executive
                </p>
              </div>

              {/* Rationale */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rationale</label>
                <textarea
                  value={referenceFormData.human_rationale}
                  onChange={(e) => setReferenceFormData(prev => ({ ...prev, human_rationale: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Explain your scoring reasoning..."
                />
              </div>

              {/* Optional Sub-Scores */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub-Scores <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'human_trajectory_score', label: 'Trajectory' },
                    { key: 'human_velocity_score', label: 'Velocity' },
                    { key: 'human_scope_score', label: 'Scope' },
                    { key: 'human_recency_score', label: 'Recency' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-500 mb-1">{label}</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={referenceFormData[key]}
                        onChange={(e) => setReferenceFormData(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="0-10"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowReferenceModal(false);
                    setReferenceError(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReferenceScore}
                  disabled={referenceSubmitting || !referenceFormData.human_score}
                  className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {referenceSubmitting ? 'Saving...' : (referenceScore ? 'Update Score' : 'Save Score')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecProfileViewer;
